import { useCallback, useMemo, useState } from 'react';

import type { IToastAction } from '@feathr/components';
import { toast } from '@feathr/components';
import { useUser } from '@feathr/extender/state';
import { logError, moment, TimeFormat } from '@feathr/hooks';
import { validate } from '@feathr/rachis';

import { useTimeFrameSelect } from './useTimeFrameSelect';

export interface IDateRangeOption {
  id: string;
  name: string;
  value: [string | undefined, string | undefined];
}

export interface IExportModalValidationErrors {
  email?: string[];
  dateRange?: string[];
  exportName?: string[];
}

interface IResolvedDateRange {
  startDate: string | undefined;
  endDate: string | undefined;
}

export interface IHandleExportOptions {
  successToastAction?: IToastAction;
  successMessage?: string;
  /**
   * Replaces the generic error toast so a caller can branch on the failure —
   * e.g. surface the API's own message, or a tailored one for a specific status.
   * Sentry reporting happens either way.
   */
  onError?: (error: unknown) => void;
}

export interface IUseExportModalOptions {
  /** When provided, the hook tracks an export-name field and blocks submit while empty. */
  initialExportName?: string;
  /** Allow-list of `useTimeFrameSelect` preset IDs; omit for all. */
  presetIds?: string[];
  /** Default selected date range id. Defaults to `'all_time'`. */
  defaultDateRange?: string;
}

interface IUseExportModalReturn {
  email: string;
  emailHadFocus: boolean;
  exportName: string;
  exportNameHadFocus: boolean;
  customDateRange: [string | undefined, string | undefined];
  dateRangeHadFocus: boolean;
  selectedDateRange: string;
  isLoading: boolean;
  isSubmitDisabled: boolean;
  validationErrors: IExportModalValidationErrors | undefined;
  dateRangeOptions: IDateRangeOption[];
  handleEmailBlur: () => void;
  handleChangeEmail: (newEmail?: string) => void;
  handleChangeExportName: (newName?: string) => void;
  handleExportNameBlur: () => void;
  handleDateRangeChange: (option: IDateRangeOption) => void;
  handleCustomDateRangeChange: (value: [string | undefined, string | undefined]) => void;
  handleExport: (
    exportFn: () => Promise<unknown>,
    onSuccess: () => void,
    t: (key: string, options?: Record<string, unknown>) => string,
    options?: IHandleExportOptions,
  ) => Promise<void>;
  setIsLoading: (loading: boolean) => void;
  resetFormState: () => void;
  resolveDateRange: () => IResolvedDateRange;
}

export function useExportModal(
  t: (key: string) => string,
  options: IUseExportModalOptions = {},
): IUseExportModalReturn {
  const { initialExportName, presetIds, defaultDateRange = 'all_time' } = options;
  const trackExportName = initialExportName !== undefined;
  const user = useUser();
  const userTimezone = user.get('timezone');
  const { options: timeFrameOptions } = useTimeFrameSelect();

  const [email, setEmail] = useState<string>(user.email);
  const [emailHadFocus, setEmailHadFocus] = useState<boolean>(false);
  const [exportName, setExportName] = useState<string>(initialExportName ?? '');
  const [exportNameHadFocus, setExportNameHadFocus] = useState<boolean>(false);
  const [customDateRange, setCustomDateRange] = useState<[string | undefined, string | undefined]>([
    undefined,
    undefined,
  ]);
  const [dateRangeHadFocus, setDateRangeHadFocus] = useState<boolean>(false);
  const [selectedDateRange, setSelectedDateRange] = useState<string>(defaultDateRange);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const filteredTimeFrameOptions = useMemo(
    () =>
      presetIds === undefined
        ? timeFrameOptions
        : timeFrameOptions.filter((opt) => presetIds.includes(opt.id)),
    [timeFrameOptions, presetIds],
  );

  const dateRangeOptions: IDateRangeOption[] = [
    { id: 'all_time', name: t('All time'), value: [undefined, undefined] },
    ...filteredTimeFrameOptions.map((opt) => ({
      id: opt.id,
      name: opt.name,
      value: opt.value as [string | undefined, string | undefined],
    })),
    { id: 'custom', name: t('Custom'), value: [undefined, undefined] },
  ];

  const handleEmailBlur = useCallback((): void => {
    setEmailHadFocus(true);
  }, []);

  const handleChangeEmail = useCallback((newEmail?: string): void => {
    setEmail(newEmail ?? '');
    setEmailHadFocus(true);
  }, []);

  const handleChangeExportName = useCallback((newName?: string): void => {
    setExportName(newName ?? '');
    setExportNameHadFocus(true);
  }, []);

  const handleExportNameBlur = useCallback((): void => {
    setExportNameHadFocus(true);
  }, []);

  const handleDateRangeChange = useCallback((option: IDateRangeOption): void => {
    setSelectedDateRange(option.id);
  }, []);

  const handleCustomDateRangeChange = useCallback(
    (value: [string | undefined, string | undefined]): void => {
      setCustomDateRange(value);
      setDateRangeHadFocus(true);
    },
    [],
  );

  const resetFormState = useCallback((): void => {
    setEmail(user.email);
    setEmailHadFocus(false);
    setExportName(initialExportName ?? '');
    setExportNameHadFocus(false);
    setCustomDateRange([undefined, undefined]);
    setDateRangeHadFocus(false);
    setSelectedDateRange(defaultDateRange);
  }, [user.email, initialExportName, defaultDateRange]);

  const resolveDateRange = useCallback((): IResolvedDateRange => {
    if (selectedDateRange === 'all_time') {
      return { startDate: undefined, endDate: undefined };
    }

    if (selectedDateRange === 'custom') {
      return { startDate: customDateRange[0], endDate: customDateRange[1] };
    }

    const selectedOption = filteredTimeFrameOptions.find((opt) => opt.id === selectedDateRange);
    if (selectedOption?.value && Array.isArray(selectedOption.value)) {
      return { startDate: selectedOption.value[0], endDate: selectedOption.value[1] };
    }

    return { startDate: undefined, endDate: undefined };
  }, [selectedDateRange, customDateRange, filteredTimeFrameOptions]);

  const validateForm = useCallback((): IExportModalValidationErrors | undefined => {
    const emailConstraints = {
      presence: { allowEmpty: false, message: '^Email address cannot be empty.' },
      email: { message: '^Email address is invalid.' },
    };

    const baseErrors = validate({ email }, { email: emailConstraints }) ?? {};

    const errors: IExportModalValidationErrors = {};
    if (baseErrors.email) {
      errors.email = baseErrors.email;
    }

    if (trackExportName && exportName.trim().length === 0) {
      errors.exportName = ['Export name cannot be empty.'];
    }

    if (selectedDateRange === 'custom') {
      const startDate = customDateRange[0];
      const endDate = customDateRange[1];
      // TODO: swap to a shared getToday util once the local-vs-user-time decision lands.
      const today = (userTimezone ? moment().tz(userTimezone) : moment()).format(
        TimeFormat.isoDate,
      );

      if (!startDate || !endDate) {
        errors.dateRange = ['Both start and end dates are required for custom range.'];
      } else if (startDate > endDate) {
        errors.dateRange = ['Start date must be before end date.'];
      } else if (startDate > today || endDate > today) {
        errors.dateRange = ['Dates cannot be in the future.'];
      }
    }

    return Object.keys(errors).length > 0 ? errors : undefined;
  }, [email, exportName, trackExportName, selectedDateRange, customDateRange, userTimezone]);

  const validationErrors = validateForm();

  const isSubmitDisabled = useMemo(
    () => isLoading || validationErrors !== undefined,
    [isLoading, validationErrors],
  );

  const handleExport = useCallback(
    async (
      exportFn: () => Promise<unknown>,
      onSuccess: () => void,
      t: (key: string, options?: Record<string, unknown>) => string,
      handleExportOptions: IHandleExportOptions = {},
    ): Promise<void> => {
      const { successToastAction, successMessage, onError } = handleExportOptions;
      try {
        setIsLoading(true);
        await exportFn();
        toast(successMessage ?? t('Started export. Check your email.'), {
          type: 'success',
          ...(successToastAction ? { action: successToastAction } : {}),
        });
        onSuccess();
      } catch (error) {
        logError({
          message: 'Export request failed',
          level: 'error',
          extra: { error: error instanceof Error ? error.message : String(error) },
        });
        if (onError) {
          onError(error);
        } else {
          toast(t("Couldn't export data. Try again."), { type: 'error' });
        }
      } finally {
        setIsLoading(false);
      }
    },
    [],
  );

  return {
    email,
    emailHadFocus,
    exportName,
    exportNameHadFocus,
    customDateRange,
    dateRangeHadFocus,
    selectedDateRange,
    isLoading,
    isSubmitDisabled,
    validationErrors,
    dateRangeOptions,
    handleEmailBlur,
    handleChangeEmail,
    handleChangeExportName,
    handleExportNameBlur,
    handleDateRangeChange,
    handleCustomDateRangeChange,
    handleExport,
    setIsLoading,
    resetFormState,
    resolveDateRange,
  };
}
